import { useState, useEffect } from 'react'
import { BackofficeLayout } from '../../components/layout/BackofficeLayout'
import { productsApi, categoriesApi } from '@eshop/shared'

export function DashboardPage() {
  const [stats, setStats] = useState({
    total_products: 0,
    total_categories: 0,
    in_stock_products: 0,
    out_of_stock_products: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      setError(null)

      const [productsResponse, categoriesResponse] = await Promise.all([
        productsApi.getAll(),
        categoriesApi.getAll()
      ])

      const inStock = productsResponse.results.filter((p: any) => p.in_stock).length
      const outOfStock = productsResponse.results.filter((p: any) => !p.in_stock).length

      setStats({
        total_products: productsResponse.count,
        total_categories: categoriesResponse.count,
        in_stock_products: inStock,
        out_of_stock_products: outOfStock
      })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar estadísticas')
      console.error('Error loading stats:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando estadísticas...</div>
        </div>
      </BackofficeLayout>
    )
  }

  if (error) {
    return (
      <BackofficeLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadStats}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Reintentar
          </button>
        </div>
      </BackofficeLayout>
    )
  }

  return (
    <BackofficeLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Dashboard</h1>
          <p className="text-neutral-500 mt-1">Resumen de tu tienda</p>
        </div>

        {/* Stats Grid - Nolavi Design System */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            title="Total Productos"
            value={stats.total_products.toString()}
            icon="📦"
            color="blue"
          />
          <StatCard
            title="En Stock"
            value={stats.in_stock_products.toString()}
            icon="✅"
            color="green"
          />
          <StatCard
            title="Sin Stock"
            value={stats.out_of_stock_products.toString()}
            icon="⚠️"
            color="warning"
          />
          <StatCard
            title="Categorías"
            value={stats.total_categories.toString()}
            icon="🏷️"
            color="neutral"
          />
        </div>

        {/* Alert for low stock */}
        {stats.out_of_stock_products > 0 && (
          <div className="bg-warning-50 border-l-4 border-warning-500 p-5 rounded-r-xl">
            <div className="flex items-start gap-4">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-semibold text-warning-700">
                  Productos sin stock
                </h3>
                <p className="text-warning-600 mt-1">
                  Tienes {stats.out_of_stock_products} producto{stats.out_of_stock_products !== 1 ? 's' : ''} sin stock.
                  <a href="/backoffice/products" className="font-semibold underline ml-1">Ver productos</a>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info Cards - Nolavi Design System */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">
              Resumen del Catálogo
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-4 bg-neutral-50 rounded-lg">
                <span className="text-neutral-600">Productos totales</span>
                <span className="text-lg font-bold text-neutral-900">{stats.total_products}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-neutral-50 rounded-lg">
                <span className="text-neutral-600">Categorías activas</span>
                <span className="text-lg font-bold text-neutral-900">{stats.total_categories}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-success-50 rounded-lg">
                <span className="text-success-700">Productos disponibles</span>
                <span className="text-lg font-bold text-success-600">{stats.in_stock_products}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">
              Acciones Rápidas
            </h3>
            <div className="space-y-3">
              <a
                href="/backoffice/products"
                className="flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
              >
                <span className="font-medium text-blue-600">Ver todos los productos</span>
                <span className="text-blue-500 text-lg">→</span>
              </a>
              <a
                href="/backoffice/orders"
                className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors border border-neutral-200"
              >
                <span className="font-medium text-neutral-700">Gestionar órdenes</span>
                <span className="text-neutral-500 text-lg">→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </BackofficeLayout>
  )
}

interface StatCardProps {
  title: string
  value: string
  icon: string
  color: 'blue' | 'green' | 'neutral' | 'warning'
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  // Nolavi Design System colors
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    green: 'bg-success-50 border-success-500/20 text-success-600',
    neutral: 'bg-neutral-50 border-neutral-200 text-neutral-600',
    warning: 'bg-warning-50 border-warning-500/20 text-warning-600',
  }

  const valueColorClasses = {
    blue: 'text-blue-500',
    green: 'text-success-600',
    neutral: 'text-neutral-800',
    warning: 'text-warning-600',
  }

  return (
    <div className={`rounded-lg shadow-sm border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide">{title}</p>
          <p className={`mt-1 text-xl font-bold ${valueColorClasses[color]}`}>{value}</p>
        </div>
        <div className="text-2xl opacity-70">
          {icon}
        </div>
      </div>
    </div>
  )
}
